import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { NoData } from 'components/NoData'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { Flex } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { OtherText, TitleText } from 'styling/Typography'
import { IFuelDispenserRow } from 'types/fuelCustody.interface'
import { FUEL_DISPENSER_CAPTION, FUEL_DISPENSER_ROWS } from 'pages/FuelManagement/fuelTanksDemoData'
import {
    DispenserCellText,
    DispenserKpiStrip,
    DispenserName,
    DispenserNameText,
    DispenserNoteText,
} from './DispensersPanel.styled'

const driftLabel = (driftPct: number | null): string =>
    driftPct == null ? '—' : `${driftPct > 0 ? '+' : ''}${driftPct.toFixed(1)}%`

export const DispensersPanel = () => {
    const rows: IFuelDispenserRow[] = USE_OPS_MOCKS ? FUEL_DISPENSER_ROWS : []
    const overdueCount = rows.filter((row) => row.statusTone === 'danger').length
    const worstDrift = rows.reduce(
        (worst: number | null, row) =>
            row.driftPct != null && (worst == null || Math.abs(row.driftPct) > Math.abs(worst))
                ? row.driftPct
                : worst,
        null,
    )

    const columns: IHonestTableColumn<IFuelDispenserRow>[] = [
        {
            id: 'dispenser',
            header: 'Dispenser',
            sticky: true,
            minWidth: '11rem',
            cell: (item) => (
                <DispenserName>
                    <DispenserNameText>{item.name}</DispenserNameText>
                    {item.note && <DispenserNoteText>{item.note}</DispenserNoteText>}
                </DispenserName>
            ),
        },
        {
            id: 'serves',
            header: 'Serves',
            minWidth: '10rem',
            cell: (item) => <DispenserCellText>{item.serves}</DispenserCellText>,
        },
        {
            id: 'lastTest',
            header: 'Last test',
            cell: (item) => (
                <DispenserCellText $tone={item.statusTone === 'danger' ? 'danger' : undefined}>
                    {item.lastTest}
                </DispenserCellText>
            ),
        },
        {
            id: 'drift',
            header: 'Drift',
            align: 'right',
            cell: (item) => (
                <DispenserCellText $tone={item.statusTone === 'warning' ? 'warning' : undefined}>
                    {driftLabel(item.driftPct)}
                </DispenserCellText>
            ),
        },
        {
            id: 'result',
            header: 'Result',
            cell: (item) =>
                item.result === 'PASS' ? (
                    <StatusPill status='success'>PASS</StatusPill>
                ) : (
                    <DispenserCellText>{item.result}</DispenserCellText>
                ),
        },
        {
            id: 'nextDue',
            header: 'Next due',
            cell: (item) => <DispenserCellText>{item.nextDue}</DispenserCellText>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatusPill status={item.statusTone}>{item.statusLabel}</StatusPill>,
        },
    ]

    if (!USE_OPS_MOCKS) {
        return <NoData text='Dispenser calibration records appear once proving-can tests are logged.' />
    }

    return (
        <Flex column gap='1rem' width='100%'>
            <DispenserKpiStrip>
                <MetricCard value={rows.length} label='DISPENSERS' sublabel='monthly proving-can cadence' accentColor={tokens.accent.base} />
                <MetricCard
                    value={overdueCount}
                    label='TESTS OVERDUE'
                    sublabel={overdueCount ? 'BOWSER-01 meter — 65 days' : 'all on schedule'}
                    accentColor={overdueCount ? tokens.status.danger.fg : tokens.status.success.fg}
                />
                <MetricCard
                    value={driftLabel(worstDrift)}
                    label='WORST DRIFT'
                    sublabel='Workshop station — approaching ±2% limit'
                    accentColor={tokens.status.warning.fg}
                />
            </DispenserKpiStrip>
            <Container>
                <ContainerHeader>
                    <Flex center gap='0.75rem'>
                        <TitleText>Dispensing points — meter calibration</TitleText>
                        <DemoBadge />
                    </Flex>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <OtherText grey>{FUEL_DISPENSER_CAPTION}</OtherText>
                    <HonestTable
                        columns={columns}
                        data={rows}
                        keyField='id'
                        emptyMessage='No dispenser tests recorded yet.'
                    />
                </ContainerContent>
            </Container>
        </Flex>
    )
}
